import { Pager } from "@d12labs/ui/pager";
import { cn } from "@d12labs/utils";
import { Link, useNavigate } from "@tanstack/react-router";
import { Star } from "lucide-react";
import type { MouseEvent } from "react";
import { fmtActive, fmtCount, fmtUsd, harnessLabel } from "../aggregate";
import type { LedgerSession } from "../api";
import { defaultDir, type SortDir, type SortKey, sortSessions } from "../overview";
import { dayLabel, HarnessDot } from "./spend-summary";

export const PAGE_SIZE = 25;

const COLUMNS: { key: SortKey; label: string; align?: "right"; width: string }[] = [
	{ key: "first", label: "Started", width: "w-[72px]" },
	{ key: "title", label: "Session", width: "" },
	{ key: "repo", label: "Repo", width: "w-[160px]" },
	{ key: "harness", label: "Harness", width: "w-[124px]" },
	{ key: "host", label: "Host", width: "w-[88px]" },
	{ key: "userPrompts", label: "Prompts", align: "right", width: "w-[72px]" },
	{ key: "activeMin", label: "Active", align: "right", width: "w-[80px]" },
	{ key: "cost", label: "Cost", align: "right", width: "w-[92px]" },
];

const SORT_NAME: Record<SortKey, string> = {
	first: "start",
	title: "title",
	repo: "repo",
	harness: "harness",
	host: "host",
	userPrompts: "prompts",
	activeMin: "active",
	cost: "cost",
};

export type TableState = { sort: SortKey; dir: SortDir; page: number };

/**
 * Filtered sessions, 36px rows, paginated. Controlled: sort, direction and the
 * 1-based page live in the URL, so browser back from a session restores them.
 * A row opens its session; the title is a real link for keyboard, screen
 * readers and middle-click.
 */
export function SessionsTable({
	sessions,
	id,
	sort,
	dir,
	page,
	onChange,
}: TableState & {
	sessions: LedgerSession[];
	id: string;
	onChange: (next: TableState) => void;
}) {
	const navigate = useNavigate();
	const sortDir = dir === "asc" ? 1 : -1;
	const sorted = sortSessions(sessions, sort, sortDir);
	const pages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
	// A stale ?page=9 past the end shows the last page, never an empty table.
	const current = Math.min(Math.max(page, 1), pages) - 1;
	const rows = sorted.slice(current * PAGE_SIZE, (current + 1) * PAGE_SIZE);
	const start = sorted.length ? current * PAGE_SIZE + 1 : 0;

	const sortBy = (key: SortKey) =>
		onChange({
			sort: key,
			dir: key === sort ? (dir === "asc" ? "desc" : "asc") : defaultDir(key),
			page: 1,
		});

	const openRow = (event: MouseEvent<HTMLTableRowElement>, sessionId: string) => {
		// The title link navigates on its own; a second push here would make
		// browser back land on the session again.
		if ((event.target as HTMLElement).closest("a")) return;
		navigate({ to: "/ledger/sessions/$id", params: { id: sessionId } });
	};

	return (
		<section
			id={id}
			aria-label="Sessions"
			className="scroll-mt-4 overflow-hidden rounded-lg border border-hairline bg-surface-1"
		>
			<div className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline px-4 py-2.5">
				<div className="flex items-baseline gap-2">
					<h2 className="card-title text-foreground">Sessions</h2>
					<span className="text-meta tabular-nums text-ink-subtle">
						{sessions.length.toLocaleString("en-US")} · sorted by {SORT_NAME[sort]}
					</span>
				</div>
				<span className="inline-flex items-center gap-1.5 text-meta text-ink-subtle">
					<Star aria-hidden className="size-3 fill-current text-primary-hover" />
					split across days in the chart
				</span>
			</div>
			<div className="overflow-x-auto">
				<table className="w-full min-w-[840px] table-fixed text-body-sm">
					<thead>
						<tr className="h-[30px] border-b border-hairline text-left text-[11px] uppercase tracking-[0.06em] text-ink-tertiary">
							{COLUMNS.map((column, i) => (
								<th
									key={column.key}
									scope="col"
									aria-sort={
										sort === column.key ? (sortDir < 0 ? "descending" : "ascending") : undefined
									}
									className={cn(
										"font-medium",
										column.width,
										i === 0 ? "pl-4 pr-3" : i === COLUMNS.length - 1 ? "pl-3 pr-4" : "px-3",
										column.align === "right" && "text-right",
									)}
								>
									<button
										type="button"
										onClick={() => sortBy(column.key)}
										className="whitespace-nowrap uppercase outline-none hover:text-foreground focus-visible:text-foreground focus-visible:underline"
									>
										{column.label}
										{sort === column.key ? (sortDir < 0 ? " ↓" : " ↑") : ""}
									</button>
								</th>
							))}
						</tr>
					</thead>
					<tbody>
						{rows.map((session) => {
							const days = Object.keys(session.days).sort();
							const split = days.length > 1;
							return (
								// The row click is a mouse shortcut; the title <Link> is the keyboard path.
								<tr
									key={session.id}
									onClick={(event) => openRow(event, session.id)}
									className="h-9 cursor-pointer border-b border-hairline last:border-0 hover:bg-surface-2"
								>
									<td className="pl-4 pr-3 font-mono text-[11.5px] text-ink-subtle">
										{dayLabel(session.first)}
									</td>
									<td className="px-3">
										<div className="flex min-w-0 items-center gap-2">
											<Link
												to="/ledger/sessions/$id"
												params={{ id: session.id }}
												className="truncate font-medium text-foreground outline-none hover:underline focus-visible:underline"
												title={session.title ?? ""}
											>
												{session.title ?? "(untitled)"}
											</Link>
											<span className="flex-none font-mono text-[10.5px] text-ink-tertiary">
												{session.id.slice(0, 8)}
											</span>
										</div>
									</td>
									<td className="truncate px-3 text-ink-muted" title={session.repo}>
										{session.repo}
									</td>
									<td className="px-3">
										<span className="inline-flex items-center gap-1.5 text-ink-muted">
											<HarnessDot harness={session.harness} />
											{harnessLabel(session.harness)}
										</span>
									</td>
									<td className="truncate px-3 text-ink-subtle">{session.host}</td>
									<td className="px-3 text-right tabular-nums text-ink-muted">
										{fmtCount(session.userPrompts)}
									</td>
									<td className="px-3 text-right tabular-nums text-ink-muted">
										{fmtActive(session.activeMin)}
									</td>
									<td
										className="pl-3 pr-4 text-right font-medium tabular-nums text-foreground"
										title={
											split
												? days.map((day) => `${day}: ${fmtUsd(session.days[day] ?? 0)}`).join("\n")
												: undefined
										}
									>
										<span className="inline-flex items-center justify-end gap-1">
											{split ? (
												<Star
													aria-label="Split across days"
													className="size-2.5 fill-current text-primary-hover"
												/>
											) : null}
											{fmtUsd(session.cost)}
										</span>
									</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>
			{/* `current` is 0-based; <Pager> is 1-based — convert here, never inside it. */}
			<Pager
				className="border-t border-hairline px-4 py-2.5 text-meta text-ink-subtle"
				page={current + 1}
				pageCount={pages}
				onPageChange={(p) => onChange({ sort, dir, page: p })}
				variant="secondary"
				label={
					<span className="tabular-nums">
						Showing {start.toLocaleString("en-US")}–
						{(start ? start + rows.length - 1 : 0).toLocaleString("en-US")} of{" "}
						{sorted.length.toLocaleString("en-US")}
					</span>
				}
			/>
		</section>
	);
}
